import React from "react";
import { Box, Container, Typography, styled, useTheme } from "@mui/material";
import { SectionTitle } from "../../../components/home-section-title/HomeSectionTitle";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import usePartners from "../../../hooks/api-request/usePartners";
import PartnersSkeleton from "../../../components/skeletons/PartnersSkeleton";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const PartnerCard = styled(motion.div)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  height: "120px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: theme.spacing(1),
  transition: "all 0.3s ease-in-out",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    transform: "translateY(-4px)",
  },
}));

const PartnerLogo = styled("img")({
  width: "100%",
  height: "auto",
  maxWidth: "160px",
  maxHeight: "80px",
  objectFit: "contain",
  transition: "transform 0.4s ease-in-out",
  "&:hover": {
    transform: "scale(1.05)",
  },
});

const SliderWrapper = styled(Box)(({ theme }) => ({
  ".slick-track": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  ".slick-slide": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(1),
    "& > div": {
      width: "100%",
    },
  },
  ".slick-dots": {
    bottom: -40,
    "& li button:before": {
      fontSize: 8,
      color: theme.palette.primary.main,
    },
    "& li.slick-active button:before": {
      color: theme.palette.primary.main,
    },
  },
}));

const SectionWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(8, 0),
  backgroundColor: theme.palette.background.default,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      ${theme.palette.divider} 0%, 
      ${theme.palette.primary.main}40 50%, 
      ${theme.palette.divider} 100%
    )`,
  },
}));

const OurPartners = () => {
  const { data: partners, isLoading } = usePartners();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  
  // Check if the current language is RTL
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    pauseOnHover: true,
    cssEase: "cubic-bezier(0.87, 0, 0.13, 1)",
    rtl: isRtl, // Set RTL for slider based on language
    responsive: [
      {
        breakpoint: theme.breakpoints.values.xl,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: theme.breakpoints.values.lg,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: theme.breakpoints.values.md,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: theme.breakpoints.values.sm,
        settings: {
          slidesToShow: 1,
          dots: true,
        },
      },
    ],
  };

  if (isLoading) {
    return <PartnersSkeleton />;
  }

  if (!partners?.length) {
    return null;
  }

  return (
    <SectionWrapper>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <SectionTitle variant="h2" sx={{ mb: 2 }}>
            {t('home.ourPartners.title')}
          </SectionTitle>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.8,
            }}
          >
            {t('home.ourPartners.subtitle')}
          </Typography>
        </Box>

        <SliderWrapper>
          <Slider {...settings}>
            {partners.map((partner, index) => (
              <Box key={partner.id}>
                <PartnerCard
                  whileHover={{ y: -4 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={partner.name}
                  >
                    <PartnerLogo
                      src={partner.logo}
                      alt={partner.name}
                      loading="lazy"
                    />
                  </a>
                </PartnerCard>
              </Box>
            ))}
          </Slider>
        </SliderWrapper>
      </Container>
    </SectionWrapper>
  );
};

export default React.memo(OurPartners);
